// ----------------------------------------------------------------------

type Props = {
  currentUserId: string;
  message: any;
  participants: any;
  loginApiData: any;
};

export const truncateText = (text: string, maxLength = 60) =>
  text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

const getFullName = (user: any) => {
  const first = user?.firstName || '';
  const last = user?.lastName || '';
  return (first + ' ' + last).trim() || 'Unknown';
};

function getQuotedSenderName(loginApiData: any, message: any, participants: any): string {
  const quotedSenderId = message?.quotedMessage?.senderId;
  const messageType = message?.messageType;

  // Case 1: If the quoted sender is the logged-in user
  if (quotedSenderId === loginApiData?.id) {
    return 'You';
  }

  // Case 2: If the quoted sender is the participant's contact
  if (quotedSenderId === participants?.contact?.id) {
    const firstName = participants?.contact?.firstName || '';
    const lastName = participants?.contact?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }

  // Case 3: If the message is inbound, use the full name from quoted user
  if (messageType === 'INBOUND') {
    return getFullName(message?.quotedMessage?.user);
  }

  // Case 4: If the message is outbound, use full name from quoted user (unless it's you, already handled above)
  if (messageType === 'OUTBOUND') {
    return getFullName(message?.quotedMessage?.user);
  }

  // Fallback
  return 'Unknown';
}

export function getMessage({ message, participants, currentUserId, loginApiData }: Props) {
  const isCurrentUser = message.messageType === 'OUTBOUND';

  const senderDetails = isCurrentUser
    ? {
        avatarUrl:
          loginApiData?.id === message?.senderId
            ? loginApiData?.ProfileURL || loginApiData?.firstName || ''
            : message?.user?.ProfileURL || getFullName(message?.user),
        firstName: message?.userId === loginApiData?.id ? 'You' : getFullName(message?.user),
      }
    : {
        avatarUrl:
          participants?.contact?.ProfileURL ||
          participants?.contact?.firstName ||
          participants?.contact?.lastName ||
          '',
        firstName:
          participants?.contact?.firstName?.split(' ')?.[0] ||
          participants?.contact?.lastName?.split(' ')?.[0] ||
          '',
      };

  const hasImage = message.contentType === 'image';
  const attachments: any[] =
    message?.MessageAttachment && message?.MessageAttachment?.length > 0
      ? message?.MessageAttachment
      : [];

  const markdownToHtml = (text: string) => {
    if (!text) return '';
    return text
      .replace(/\*(.*?)\*/g, '<b>$1</b>')
      .replace(/_(.*?)_/g, '<i>$1</i>')
      .replace(/~(.*?)~/g, '<s>$1</s>');
  };
  const updateMessage = {
    ...message,
    text: message?.text ? markdownToHtml(message.text) : '',
    ...(message.quotedMessage &&
      message?.quotedId && {
        quotedMessage: {
          ...message?.quotedMessage,
          text: message?.quotedMessage?.text ? markdownToHtml(message?.quotedMessage?.text) : '',
          senderName: getQuotedSenderName(loginApiData, message, participants),
        },
      }),
  };

  return { hasImage, me: isCurrentUser, senderDetails, attachments, updateMessage };
}
